// The Dashboard — the landing surface, a Settings-style accordion whose sections
// can be pinned to the top. Sections: System overview (notification feed + today's
// score + connected devices), Clock (world clocks + sessions), Calendar (econ
// schedule), and Armed alerts (the live watchers armed from the Strategy Lab).
// Clock / Calendar / Alerts mount once; only the overview rebuilds on the tick.

import { el, svgIcon, pressable, closeButton } from "../lib/dom.js";
import { storageGet, storageSet } from "../lib/storage.js";
import { journalStore } from "../lib/journal-store.js";
import { todayStats, isAfterClose } from "../lib/today.js";
import { fetchServerStatus, isOnline, type ServerStatus } from "../lib/trading-server.js";
import { confluenceStore } from "../lib/confluence-store.js";
import { notificationStore, categoryIcon, type AppNotification } from "../lib/notifications-store.js";
import { renderClockContent } from "./clock.js";
import { renderCalendar } from "./markets.js";
import { renderJournalPage } from "./journal.js";
import { strategyStore, getSignalSubscriptions, toggleSignalSubscription } from "../lib/strategy.js";

type SectionKey = "overview" | "forex" | "stocks" | "calendar" | "alerts" | "journal";
const TITLES: Record<SectionKey, string> = { overview: "System overview", forex: "Forex & commodities", stocks: "Stocks", calendar: "Calendar", alerts: "Armed alerts", journal: "Journal" };
// Clocks (forex/stocks) and Calendar hidden for launch — re-add their keys here
// to bring the tabs back (bodies still mount, so it's a one-line re-reveal).
const SECTION_ORDER: SectionKey[] = ["overview", "alerts", "journal"];
// Toolbar glyphs (16 viewBox, stroked) — one per section. Forex&commodities =
// a globe (24h global markets); Stocks = a bar chart.
const SECTION_ICONS: Record<SectionKey, string> = {
  overview: "M2 8h2.4l1.8-4.2 3 8.4 1.8-4.2H14",
  forex: "M8 2a6 6 0 1 0 0 12A6 6 0 0 0 8 2M2 8h12M8 2c1.7 1.6 2.5 3.7 2.5 6s-.8 4.4-2.5 6M8 2C6.3 3.6 5.5 5.7 5.5 8s.8 4.4 2.5 6",
  stocks: "M2.5 13.5V8.5M6 13.5V4.5M9.5 13.5V6.5M13 13.5V2.5",
  calendar: "M3.5 4h9v8.5h-9zM3.5 6.5h9M6 2.4v2M10 2.4v2",
  alerts: "M8 2.6a3 3 0 0 0-3 3v2.2L3.8 11h8.4L11 7.8V5.6a3 3 0 0 0-3-3M6.7 11a1.3 1.3 0 0 0 2.6 0",
  // The Journal (book) glyph — same mark used elsewhere for the Journal.
  journal: "M8 4C6.6 3.1 4.4 3.1 2.8 3.6v8c1.6-.5 3.8-.5 5.2.4M8 4c1.4-.9 3.6-.9 5.2-.4v8c-1.6-.5-3.8-.5-5.2.4M8 4v8",
};

export async function renderTodayPage(root: HTMLElement): Promise<() => void> {
  await journalStore.init();
  await confluenceStore.init();
  await notificationStore.init();
  await strategyStore.init();
  let serverStatus: ServerStatus | null = null;

  // The section shown below the toolbar. Defaults to the overview; a chart's
  // calendar marker can ask for the Calendar section directly.
  let activeKey: SectionKey = "overview";
  const openReq = await storageGet<{ section?: string } | null>("dashboardOpen", null);
  if ((openReq?.section === "calendar" || openReq?.section === "markets") && SECTION_ORDER.includes("calendar")) activeKey = "calendar";
  if (openReq) void storageSet("dashboardOpen", null);

  const sectionLabel = (text: string): HTMLElement =>
    el("div", { text, style: { font: "var(--font-label)", color: "var(--text-secondary)", letterSpacing: "0.06em", textTransform: "uppercase", padding: "2px 2px 0" } });

  const card = (children: HTMLElement[]): HTMLElement =>
    el("div", { style: { background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: "12px", padding: "10px 12px", display: "flex", flexDirection: "column", gap: "6px" } }, children);

  const kv = (label: string, value: string, color = "var(--text-primary)"): HTMLElement =>
    el("div", { style: { display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "baseline" } }, [
      el("span", { text: label, style: { font: "var(--font-caption)", color: "var(--text-secondary)" } }),
      el("span", { text: value, style: { font: "var(--font-caption)", color, fontVariantNumeric: "tabular-nums" } }),
    ]);

  const timeAgo = (ms: number): string => {
    const s = Math.floor((Date.now() - ms) / 1000);
    if (s < 60) return "just now";
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  function notifBubble(n: AppNotification): HTMLElement {
    const bubble = el("div", {
      style: { display: "flex", gap: "10px", alignItems: "flex-start", padding: "9px 10px", background: "var(--bg-fill)", borderRadius: "10px", cursor: "pointer" },
      onClick: () => window.dispatchEvent(new CustomEvent("tickahead:navigate", { detail: n.targetPage })),
    }, [
      el("div", { text: categoryIcon[n.category], style: { fontSize: "14px", flex: "none", lineHeight: "1.3" } }),
      el("div", { style: { display: "flex", flexDirection: "column", gap: "2px", flex: "1", minWidth: "0" } }, [
        el("div", { text: n.title, style: { font: "var(--font-header)" } }),
        el("div", { text: n.message, style: { font: "var(--font-caption)", color: "var(--text-secondary)" } }),
        el("div", { text: timeAgo(n.date), style: { font: "var(--font-caption)", color: "var(--text-secondary-faint)" } }),
      ]),
    ]);
    const x = el("button", { attrs: { "aria-label": "Dismiss" }, style: { color: "var(--text-secondary)", display: "flex", flex: "none" } });
    x.appendChild(svgIcon("M4 4l8 8M12 4l-8 8", 10));
    x.addEventListener("click", (e) => { e.stopPropagation(); void notificationStore.dismiss(n.id); });
    bubble.appendChild(x);
    return bubble;
  }

  function notifSection(): HTMLElement | null {
    const list = notificationStore.notifications;
    if (!list.length) return null;
    const wrap = el("div", { style: { display: "flex", flexDirection: "column", gap: "8px" } });
    wrap.appendChild(el("div", { style: { display: "flex", alignItems: "baseline", justifyContent: "space-between" } }, [
      sectionLabel("Notifications"),
      el("button", { text: "Clear all", style: { font: "var(--font-caption)", color: "var(--accent-blue)", cursor: "pointer" }, onClick: () => { for (const n of [...list]) void notificationStore.dismiss(n.id); } }),
    ]));
    for (const n of list) wrap.appendChild(notifBubble(n));
    return wrap;
  }

  function todayCard(now: Date): HTMLElement | null {
    const stats = todayStats(journalStore.entries, now);
    if (stats.trades === 0) return null;
    const closed = isAfterClose(now);
    const c = el("div", {
      style: {
        background: "var(--bg-fill)",
        borderLeft: `3px solid ${stats.totalR >= 0 ? "var(--up)" : "var(--down)"}`,
        borderRadius: "0 10px 10px 0",
        padding: "10px 12px",
        display: "flex",
        flexDirection: "column",
        gap: "6px",
      },
    });
    c.appendChild(el("div", { text: closed ? "Today's score" : "Today so far", style: { font: "var(--font-label)", color: "var(--gold)", letterSpacing: "0.08em", textTransform: "uppercase" } }));
    const fmtR = (v: number) => `${v > 0 ? "+" : ""}${v.toFixed(1)}R`;
    c.appendChild(el("div", { text: [`${stats.trades} trade${stats.trades === 1 ? "" : "s"}`, fmtR(stats.totalR)].join(" · "), style: { font: "var(--font-body)", color: stats.totalR >= 0 ? "var(--up)" : "var(--down)", fontVariantNumeric: "tabular-nums" } }));
    if (closed && stats.best && stats.worst && stats.trades >= 2) {
      c.appendChild(el("div", { text: `Best: ${stats.best.symbol} ${fmtR(stats.best.rMultiple)} · Worst: ${stats.worst.symbol} ${fmtR(stats.worst.rMultiple)}`, style: { font: "var(--font-caption)", color: "var(--text-secondary)" } }));
    }
    if (closed && stats.lastLesson) {
      c.appendChild(el("div", { text: `Tomorrow's reminder: “${stats.lastLesson}”`, style: { font: "var(--font-body)", color: "var(--text-secondary)", fontStyle: "italic" } }));
    }
    return c;
  }

  function devicesSection(now: Date): HTMLElement | null {
    const boxes: HTMLElement[] = [];
    if (serverStatus?.paired) {
      const online = isOnline(serverStatus.lastSeen != null ? serverStatus.lastSeen * 1000 : undefined, now.getTime());
      const hb = serverStatus.status ?? {};
      const head = el("div", { style: { display: "flex", alignItems: "center", gap: "8px" } }, [
        el("div", { style: { width: "8px", height: "8px", borderRadius: "50%", flex: "none", background: online ? "var(--up)" : "var(--text-secondary-faint)" } }),
        el("div", { text: serverStatus.name ?? "Trading server", style: { font: "var(--font-header)", flex: "1", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } }),
        el("div", { text: online ? "Online" : "Offline", style: { font: "var(--font-caption)", color: online ? "var(--up)" : "var(--text-secondary)" } }),
      ]);
      const rows: HTMLElement[] = [head];
      if (hb.strategy) rows.push(kv("Strategy", String(hb.strategy)));
      if (hb.account) rows.push(kv("Account", String(hb.account)));
      if (hb.mode) rows.push(kv("Mode", String(hb.mode)));
      if (hb.state) rows.push(kv("State", String(hb.state)));
      const openN = typeof hb.openPositions === "number" ? hb.openPositions : Array.isArray(hb.openPositions) ? hb.openPositions.length : undefined;
      if (openN != null) rows.push(kv("Open positions", String(openN)));
      if (typeof hb.pnlToday === "number") rows.push(kv("P&L today", `${hb.pnlToday >= 0 ? "+" : ""}${hb.pnlToday}`, hb.pnlToday >= 0 ? "var(--up)" : "var(--down)"));
      boxes.push(card(rows));
    }
    if (!boxes.length) return null;
    const wrap = el("div", { style: { display: "flex", flexDirection: "column", gap: "8px" } });
    wrap.appendChild(sectionLabel("Connected"));
    for (const b of boxes) wrap.appendChild(b);
    return wrap;
  }

  // ---- section bodies ----------------------------------------------------
  // Bodies fill the content area (flex:1) so an empty state can centre itself.
  const overviewBody = el("div", { style: { display: "flex", flexDirection: "column", gap: "12px", flex: "1", minHeight: "0" } });
  const forexBody = el("div", { style: { display: "flex", flexDirection: "column", flex: "1", minHeight: "0" } });
  const stocksBody = el("div", { style: { display: "flex", flexDirection: "column", flex: "1", minHeight: "0" } });
  const calendarBody = el("div", { style: { display: "flex", flexDirection: "column", flex: "1", minHeight: "0" } });
  const alertsBody = el("div", { style: { display: "flex", flexDirection: "column", gap: "6px", paddingTop: "2px", flex: "1", minHeight: "0" } });
  const journalBody = el("div", { style: { display: "flex", flexDirection: "column", flex: "1", minHeight: "0" } });

  // "Nothing to report" copy, vertically centred like the Deploy page.
  const emptyCentered = (text: string): HTMLElement =>
    el("div", { style: { flex: "1", minHeight: "0", display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "0 24px" } }, [
      el("div", { text, style: { font: "var(--font-caption)", color: "var(--text-secondary)" } }),
    ]);
  const bodies: Record<SectionKey, HTMLElement> = { overview: overviewBody, forex: forexBody, stocks: stocksBody, calendar: calendarBody, alerts: alertsBody, journal: journalBody };

  // ---- toolbar + content (D-085) -----------------------------------------
  // A little tool bar of icon buttons (chart tool-panel style) sits at the top;
  // pressing one swaps the content shown below it. One section visible at a time.
  const container = el("div", { style: { display: "flex", flexDirection: "column", gap: "10px", height: "100%" } });
  root.replaceChildren(container);
  // Centred toolbar in the exact Scan style (chart-style pill buttons, gap 8).
  const toolbar = el("div", { style: { display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", flex: "none" } });
  const contentHost = el("div", { className: "scroll", style: { flex: "1", minHeight: "0", display: "flex", flexDirection: "column" } });
  container.append(toolbar, contentHost);

  function showSection(): void {
    // Only the active body is mounted; don't touch its display (it must stay the
    // flex column it was built as, so empty states can centre).
    contentHost.replaceChildren(bodies[activeKey]);
  }

  function renderTabs(): void {
    toolbar.replaceChildren();
    for (const key of SECTION_ORDER) {
      const on = key === activeKey;
      const b = el("button", {
        className: "pill",
        attrs: { title: TITLES[key], "aria-label": TITLES[key] },
        style: { width: "30px", height: "30px", padding: "0", borderRadius: "999px", display: "flex", alignItems: "center", justifyContent: "center", flex: "none", color: on ? "var(--icon-active)" : "var(--text-secondary)", background: on ? "var(--bg-fill-strong)" : "" },
        onClick: () => { if (key !== activeKey) { activeKey = key; showSection(); renderTabs(); } },
      }, [svgIcon(SECTION_ICONS[key], 15)]);
      pressable(b);
      toolbar.appendChild(b);
    }
  }

  function renderAccordion(): void { renderTabs(); showSection(); }

  // Mount the once-only sections. The clock renders twice, one per fixed market.
  const forexCleanup = await renderClockContent(forexBody, { mode: "forex" });
  const stocksCleanup = await renderClockContent(stocksBody, { mode: "stocks" });
  const calendarCleanup = renderCalendar(calendarBody);
  const journalCleanup = await renderJournalPage(journalBody);

  // Armed alerts — the live watchers armed from the Strategy Lab.
  let signalSubs = await getSignalSubscriptions();
  function renderAlerts(): void {
    alertsBody.replaceChildren();
    if (!signalSubs.length) {
      alertsBody.appendChild(emptyCentered("No armed alerts."));
      return;
    }
    const tfLabel: Record<string, string> = { "5m": "5 Min", "15m": "15 Min", "30m": "30 Min", "60m": "1 Hour", "1d": "1 Day", "1wk": "1 Week" };
    for (const sub of signalSubs) {
      const strategy = strategyStore.strategies.find((st) => st.id === sub.strategyId);
      const rowEl = el("div", { className: "card", style: { display: "flex", alignItems: "center", gap: "8px", padding: "7px 10px" } });
      rowEl.appendChild(
        el("div", { style: { display: "flex", flexDirection: "column", gap: "1px", flex: "1", minWidth: "0" } }, [
          el("div", { text: strategy?.name ?? "(deleted strategy)", style: { font: "var(--font-body)", color: strategy ? "var(--text-primary)" : "var(--text-secondary-faint)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } }),
          el("div", { text: `${sub.symbol} · ${tfLabel[sub.interval] ?? sub.interval}${strategy ? "" : " — will never fire; remove it"}`, style: { font: "var(--font-caption)", color: "var(--text-secondary)" } }),
        ])
      );
      const cancelBtn = closeButton(() => {}, { title: "Stop watching this setup", label: "Cancel live alert", size: 11 });
      Object.assign(cancelBtn.style, { width: "18px", height: "18px" });
      cancelBtn.addEventListener("click", () => { void toggleSignalSubscription(sub).then(async () => { signalSubs = await getSignalSubscriptions(); renderAlerts(); }); });
      rowEl.appendChild(cancelBtn);
      alertsBody.appendChild(rowEl);
    }
  }
  renderAlerts();
  async function refreshAlerts(): Promise<void> { signalSubs = await getSignalSubscriptions(); renderAlerts(); }

  // System-overview body (rebuilt on tick).
  function tick() {
    const now = new Date();
    overviewBody.replaceChildren();
    const notifs = notifSection();
    if (notifs) overviewBody.appendChild(notifs);
    const today = todayCard(now);
    if (today) overviewBody.appendChild(today);
    const devices = devicesSection(now);
    if (devices) overviewBody.appendChild(devices);
    if (!notifs && !today && !devices) {
      overviewBody.appendChild(emptyCentered("Nothing to report."));
    }
  }

  async function refreshDevices() {
    const s = await fetchServerStatus();
    if (s) serverStatus = s;
    tick();
  }

  renderAccordion();
  tick();
  void refreshDevices();
  const interval = setInterval(tick, 30_000);
  const deviceInterval = setInterval(() => void refreshDevices(), 30_000);
  const unsubNotif = notificationStore.changed.subscribe(() => { tick(); void refreshAlerts(); });
  return () => {
    clearInterval(interval);
    clearInterval(deviceInterval);
    unsubNotif();
    forexCleanup();
    stocksCleanup();
    calendarCleanup();
    journalCleanup();
  };
}
